// frontend/src/components/hr-auto/types.ts
//
// Every TypeScript type for the HR Auto API contract lives in this one file, so the
// backend can be diffed against it line by line.
//
// CASING, AND THE ONE PLACE THE TWO SPELLINGS MEET
// Plan 02 section 4.1 draws AgentStep in camelCase (`durationMs`, `resultSummary`)
// while plan 03 section 9 draws the response around it in snake_case (`run_url`,
// `slots_captured`). The plans contradict each other. The built backend is
// snake_case at every level, consistent with every other API in this app and with
// the house style for row interfaces, so THE WIRE IS SNAKE_CASE THROUGHOUT and
// these types say so. Components read these fields directly; there is no second
// camelCase spelling of a wire field anywhere in this folder.
//
// The only translation that happens at all is `normalizeStep` / `normalizeTurn`
// below, which coerce a raw response into the declared shape: they fill defaults,
// reject an out-of-range enum, and tolerate the camelCase spellings that plan 02
// section 4.1 would have produced. That tolerance is one function in one file, and
// it exists so that a spelling disagreement costs a log line rather than a blank
// step row on a projector.
//
// Fields marked OPTIONAL EXTENSION are not in the plan's JSON sample but are needed
// to render what plan 06 section 2 draws. Each degrades to a sensible default when
// the server omits it.

// ── Systems ────────────────────────────────────────────────────────────────

/** Plan 02 section 5. The ten systems a step can be attributed to. */
export type SystemKey =
  | 'internal'
  | 'policies'
  | 'workday'
  | 'civil_service_jobs'
  | 'linkedin'
  | 'outlook'
  | 'servicenow'
  | 'sap_payroll'
  | 'active_directory'
  | 'dbs'

export const SYSTEM_KEYS: SystemKey[] = [
  'internal',
  'policies',
  'workday',
  'civil_service_jobs',
  'linkedin',
  'outlook',
  'servicenow',
  'sap_payroll',
  'active_directory',
  'dbs',
]

// ── Steps ──────────────────────────────────────────────────────────────────

export type StepStatus = 'pending' | 'running' | 'done' | 'blocked'

const STEP_STATUSES: StepStatus[] = ['pending', 'running', 'done', 'blocked']

/** Plan 02 section 4.1, respelled snake_case to match the wire. */
export interface AgentStep {
  id: string
  sequence: number
  /** "Applying policy: Vacancy Approval Gate" */
  label: string
  /** "Grade HEO or above requires finance sign-off" */
  detail?: string
  system: SystemKey
  /** The declared tool name, e.g. 'check_establishment_control'. */
  action: string
  /** 700 to 1600 from the server. Clamped and scaled by pacing.ts, never honoured blindly. */
  duration_ms: number
  status: StepStatus
  policy_id?: string
  policy_name?: string
  confidence?: number
  /** "3 of 3 checks passed" */
  result_summary?: string
}

// ── Scenarios ──────────────────────────────────────────────────────────────

/** `GET /api/hr-auto/scenarios` */
export interface HRAutoScenario {
  key: string
  chip_label: string
  chip_prompt: string
  systems: SystemKey[]
  estimated_turns: number
}

export interface ScenarioListResponse {
  scenarios: HRAutoScenario[]
}

// ── Sessions ───────────────────────────────────────────────────────────────

/** `POST /api/hr-auto/sessions`, request body. */
export interface CreateSessionRequest {
  channel: 'portal'
}

/** `POST /api/hr-auto/sessions`, response. */
export interface CreateSessionResponse {
  session_id: string
  run_id: string | null
  started_at: string
}

/** `POST /api/hr-auto/sessions/{session_id}/messages`, request body. */
export interface SendMessageRequest {
  message: string
}

// ── Artifacts ──────────────────────────────────────────────────────────────

/**
 * Plan 06 section 1 tabulates four kinds. There is a fifth.
 *
 * `decision` is what the onboarding scenario's DBS card is: a question with two
 * named answers rather than a draft to approve. It was declared on the server and
 * not here, so `normalizeRunArtifact` coerced it to `letter` and the audit panel
 * labelled a real decision a Letter, while the live card fell through to a generic
 * Draft heading with an Approve and Cancel bar that has nothing to approve.
 */
export type ArtifactKind =
  | 'job_description'
  | 'email_draft'
  | 'letter'
  | 'checklist'
  | 'decision'

export interface ArtifactRecipient {
  name: string
  address: string
}

export interface HRAutoArtifact {
  id: string
  kind: ArtifactKind
  title: string
  body: string
  requires_approval: boolean
  /** The key the follow-on turn triggers on, as `artifact_approved:<artifact_key>`. */
  artifact_key?: string

  /** OPTIONAL EXTENSION. Second line of the card header, e.g. the grade and location. */
  subtitle?: string
  /**
   * OPTIONAL EXTENSION. The evidence half of "surfaces the case with the evidence":
   * the policy names applied to this draft, rendered in the card footer.
   */
  evidence?: string[]

  // Email shape, plan 06 section 2.2. Absent on a job description.
  subject?: string
  to?: ArtifactRecipient[]
  cc?: ArtifactRecipient[]
}

// ── Turns ──────────────────────────────────────────────────────────────────

/** Plan 03 section 9. The status the server reports for the turn it just returned. */
export type TurnStatus = 'playing' | 'awaiting_approval' | 'complete'

const TURN_STATUSES: TurnStatus[] = ['playing', 'awaiting_approval', 'complete']

/** Plan 06 section 4. Present only when an email draft was approved. */
export interface SentConfirmation {
  kind: 'email_sent'
  recipients: ArtifactRecipient[]
  sent_at: string
}

/**
 * The response to a message and to an approval. One shape for both, per plan 03
 * section 9 and plan 06 section 4.
 */
export interface HRAutoTurnResponse {
  session_id: string
  run_id: string | null
  scenario_key: string | null
  message_id: string
  steps: AgentStep[]
  /** Markdown. Bold, bullets and a short table only, per plan 04 section 7. */
  reply: string
  artifact?: HRAutoArtifact | null
  suggestions?: string[]
  status: TurnStatus
  run_url?: string | null
  slots_captured?: Record<string, string>
  slots_missing?: string[]
  /** Approval responses only. */
  confirmation?: SentConfirmation | null
}

// ── Approval ───────────────────────────────────────────────────────────────

export type ApprovalAction = 'approve' | 'edit_and_approve' | 'cancel'

/** `POST /api/hr-auto/sessions/{session_id}/artifacts/{artifact_id}/approve` */
export interface ApprovalRequest {
  action: ApprovalAction
  edited_body?: string | null
  edited_subject?: string | null
  edited_recipient?: string | null
}

// ── Normalisation ──────────────────────────────────────────────────────────
//
// The only code in this folder that touches a raw response. Everything downstream
// is handed the declared types.

function oneOf<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback
}

/**
 * Coerce one raw step into `AgentStep`.
 *
 * `duration_ms` is also read from `durationMs`, and the same for the other three
 * multi-word step fields, because plan 02 section 4.1 spells them that way and a
 * step that arrives in the plan's spelling should still play rather than sit at a
 * zero duration with no label.
 */
export function normalizeStep(raw: unknown, index: number): AgentStep {
  const s = (raw ?? {}) as Record<string, unknown>
  const either = <T>(snake: string, camel: string): T | undefined =>
    (s[snake] ?? s[camel]) as T | undefined

  const duration = either<number>('duration_ms', 'durationMs')

  return {
    id: String(s.id ?? `step-${index}`),
    sequence: Number(s.sequence ?? index + 1),
    label: String(s.label ?? 'Working'),
    detail: typeof s.detail === 'string' ? s.detail : undefined,
    system: oneOf<SystemKey>(s.system, SYSTEM_KEYS, 'internal'),
    action: String(s.action ?? ''),
    duration_ms: typeof duration === 'number' && Number.isFinite(duration) ? duration : 0,
    status: oneOf<StepStatus>(s.status, STEP_STATUSES, 'done'),
    policy_id: either<string>('policy_id', 'policyId'),
    policy_name: either<string>('policy_name', 'policyName'),
    confidence: typeof s.confidence === 'number' ? s.confidence : undefined,
    result_summary: either<string>('result_summary', 'resultSummary'),
  }
}

/** Coerce one raw turn response into `HRAutoTurnResponse`. */
export function normalizeTurn(raw: unknown): HRAutoTurnResponse {
  const t = (raw ?? {}) as Record<string, unknown>
  const steps = Array.isArray(t.steps) ? t.steps.map(normalizeStep) : []

  return {
    session_id: String(t.session_id ?? ''),
    run_id: typeof t.run_id === 'string' ? t.run_id : null,
    scenario_key: typeof t.scenario_key === 'string' ? t.scenario_key : null,
    message_id: String(t.message_id ?? `turn-${Date.now()}`),
    steps,
    reply: typeof t.reply === 'string' ? t.reply : '',
    artifact: (t.artifact as HRAutoArtifact | undefined) ?? null,
    suggestions: Array.isArray(t.suggestions) ? (t.suggestions as string[]) : [],
    status: oneOf<TurnStatus>(t.status, TURN_STATUSES, 'complete'),
    run_url: typeof t.run_url === 'string' ? t.run_url : null,
    slots_captured: (t.slots_captured as Record<string, string> | undefined) ?? {},
    slots_missing: Array.isArray(t.slots_missing) ? (t.slots_missing as string[]) : [],
    confirmation: (t.confirmation as SentConfirmation | undefined) ?? null,
  }
}

// ── Client-side view state ─────────────────────────────────────────────────
//
// Below this line nothing is part of the API contract. It is the local state
// plan 02 section 6 specifies, kept here so the whole page has one vocabulary.

/** Plan 02 section 6. Drives the composer. */
export type HRAutoStatus =
  | 'idle'
  | 'sending'
  | 'playing'
  | 'awaiting_approval'
  | 'complete'

/** What has happened to an artifact card since it was rendered. */
export type ArtifactUIState =
  | 'pending'
  | 'approved'
  | 'edited_approved'
  | 'sent'
  | 'cancelled'

export interface UserMessage {
  kind: 'user'
  id: string
  text: string
}

export interface AgentMessage {
  kind: 'agent'
  id: string
  turn: HRAutoTurnResponse
  /** Steps and reply have both finished playing. */
  played: boolean
  artifactState: ArtifactUIState
  /** When the decision on this turn's artifact was taken, for the card pill. */
  decidedAt?: string
  /** The body as the presenter edited it, when they edited it. */
  editedBody?: string
  editedSubject?: string
}

export type HRAutoMessage = UserMessage | AgentMessage

// ═══════════════════════════════════════════════════════════════════════════
// STORED RUNS: the Auto Runs tab and its audit trail
// ═══════════════════════════════════════════════════════════════════════════
//
// Everything below describes `GET /api/hr-auto/runs`, `/runs/{run_id}` and
// `/runs/summary`, which read back what plan 05 section 1 persisted. It lives in
// this file, not a second contract file, so the server's schemas can be diffed
// against ONE frontend source of truth.
//
// Column for column these types follow the four tables in plan 05 section 1, in
// the same snake_case the rest of this file uses (correction C16). Fields the
// plan's table does not promise are marked OPTIONAL EXTENSION and every panel
// renders without them.
//
// The backend was written at the same time as this tab, so the normalisers below
// are deliberately forgiving: a figure that arrives under a near-miss name is
// read rather than dropped, and a missing block becomes an empty list rather than
// a crash on a projector.

/** Plan 05 section 1.1, `hr_auto_runs.status`. */
export type AutoRunStatus = 'in_progress' | 'awaiting_approval' | 'completed' | 'cancelled'

const AUTO_RUN_STATUSES: AutoRunStatus[] = [
  'in_progress',
  'awaiting_approval',
  'completed',
  'cancelled',
]

/** Plan 05 section 1.4, `hr_auto_artifacts.status`. */
export type AutoRunArtifactStatus =
  | 'draft'
  | 'approved'
  | 'edited_approved'
  | 'sent_simulated'
  | 'cancelled'

const AUTO_RUN_ARTIFACT_STATUSES: AutoRunArtifactStatus[] = [
  'draft',
  'approved',
  'edited_approved',
  'sent_simulated',
  'cancelled',
]

/** `hr_auto_messages.role`. */
export type AutoRunMessageRole = 'user' | 'ai_employee' | 'system'

const AUTO_RUN_MESSAGE_ROLES: AutoRunMessageRole[] = ['user', 'ai_employee', 'system']

/** One entry of `hr_auto_runs.policies_applied`, resolved live at run time. */
export interface AutoRunPolicyRef {
  /** Null when the policy could not be resolved by name. The panel then links nowhere. */
  id: string | null
  name: string
  /** OPTIONAL EXTENSION. `pass`, `escalate`, `approval_required` and so on. */
  outcome?: string | null
  /** OPTIONAL EXTENSION. Why the policy decided what it decided. */
  detail?: string | null
}

/** One entry of `hr_auto_runs.decisions_taken`. */
export interface AutoRunDecision {
  /** The artifact the decision was about, by key or by title. */
  artifact?: string | null
  /** `approve`, `edit_and_approve`, `cancel`. */
  action: string
  by?: string | null
  at?: string | null
  /**
   * The named answer, on a `decision` artifact only. "Raise the DBS check now" or
   * "Move the start date". Absent on an ordinary approval, where the action is the
   * whole of what happened. Without it the panel can say that somebody approved
   * the card but not which of the two they chose, which on a decision card is the
   * entire content of the decision.
   */
  option?: string | null
}

/** Plan 05 section 1.2. One turn of the conversation as it was rendered. */
export interface AutoRunMessage {
  id: string
  sequence: number
  role: AutoRunMessageRole
  /** Raw markdown, exactly as the live page rendered it. */
  content: string
  /** False for an off-script message the router did not match to a turn. */
  routed: boolean
  turn_id?: string | null
  created_at?: string | null
}

/**
 * Plan 05 section 1.3. A stored step.
 *
 * It is an `AgentStep` with the stored-only columns added, so the audit trail and
 * the live step player read the same fields and one component can draw both.
 *
 * `duration_ms` IS NOT AN ELAPSED TIME. It is the pacing instruction the client
 * was given for the live play-through, recorded so a replay looks like the
 * original. Plan 11 section 5 item 3 records that as known debt. Nothing in the
 * audit panel presents it as a measurement.
 */
export interface AutoRunStep extends AgentStep {
  /** Which agent turn this step belongs to. Groups the trail. */
  message_id?: string | null
  /**
   * The producing message's sequence, which counts across the whole run.
   *
   * `sequence` does NOT. It restarts at 1 on every turn, so it orders steps
   * within a turn and nothing else: a four turn run holds four steps numbered 1.
   * Sorting the trail by it interleaves the turns, which is what produced
   * seventeen one-step groups out of seventeen steps. Run order is this field
   * first, then `sequence`, and the server already returns the list that way.
   */
  turn_sequence?: number | null
  /** The executor's return payload, shown behind the "show raw" toggle. */
  result?: unknown
  created_at?: string | null
  /** OPTIONAL EXTENSION. The Agent Hub operator that owns this action. */
  operator_id?: string | null
}

/** Plan 05 section 1.4. A draft, its edit and its decision. */
export interface AutoRunArtifact {
  id: string
  artifact_key?: string | null
  kind: ArtifactKind
  title: string
  recipient?: string | null
  subject?: string | null
  /** As the AI Employee proposed it. */
  body: string
  /** As the human changed it, when the human changed it. Null means unedited. */
  edited_body?: string | null
  /** OPTIONAL EXTENSION. The edited subject line of an email draft. */
  edited_subject?: string | null
  status: AutoRunArtifactStatus
  /** The approver's identity claim. An id, so it is never rendered to a person. */
  approved_by?: string | null
  /**
   * The approver as a person reads them, recorded by the same code path that
   * writes `decisions_taken.by`. Null on a row written before the column existed,
   * and the panel then shows no name at all rather than falling back to the id.
   */
  approved_by_name?: string | null
  approved_at?: string | null
  /**
   * OPTIONAL EXTENSION, plan 06 section 5. True for a send that was simulated.
   * A `sent_simulated` status means the same thing, so the panel treats either as
   * grounds to show the simulated-send line.
   */
  simulated?: boolean | null
  sent_at?: string | null
  created_at?: string | null
}

/** A row of `GET /api/hr-auto/runs`. Plan 05 section 1.1 without the children. */
export interface AutoRunListItem {
  id: string
  run_code: string
  session_id?: string | null
  scenario_key: string
  title: string
  status: AutoRunStatus
  channel: string
  initiated_by?: string | null
  initiated_by_name?: string | null
  started_at?: string | null
  completed_at?: string | null
  outcome_summary?: string | null
  systems_touched: SystemKey[]
  /**
   * UNDEFINED IS NOT THE SAME AS EMPTY, and the difference is load bearing.
   *
   * An empty array is the run saying it applied no policy and took no human
   * decision. Undefined is the payload not carrying the field at all, which the
   * list endpoint did for a while. The HR Case Management bridge reads
   * `decisions_taken` to decide whether a run was resolved autonomously, so
   * flattening the two would have it announce "no human touched this" on the
   * strength of a field nobody sent.
   */
  policies_applied?: AutoRunPolicyRef[]
  decisions_taken?: AutoRunDecision[]
  step_count: number
  /** Wall clock from the first message to the terminal turn. Null while running. */
  duration_seconds?: number | null
  requisition_id?: string | null
  employee_id?: string | null
  separation_id?: string | null
}

/** `GET /api/hr-auto/runs/{run_id}`. The list row plus everything it owns. */
export interface AutoRunDetail extends AutoRunListItem {
  messages: AutoRunMessage[]
  steps: AutoRunStep[]
  artifacts: AutoRunArtifact[]
  /** Every captured slot, the machine-readable version of the conversation. */
  slots: Record<string, string>
}

/** `GET /api/hr-auto/runs`. House shape, per correction C14. */
export interface AutoRunListResponse {
  items: AutoRunListItem[]
  pagination?: {
    page: number
    page_size: number
    total_items: number
    total_pages: number
  }
}

/**
 * `GET /api/hr-auto/runs/summary`. The four figures of plan 05 section 4.3.
 *
 * `avg_duration_seconds` is the one a stakeholder repeats, so it is the one that
 * must never be invented: null means no completed run had a duration, which the
 * band renders as "no data" rather than as zero minutes.
 */
export interface AutoRunsSummary {
  runs_completed: number
  systems_orchestrated: number
  human_decisions_recorded: number
  avg_duration_seconds: number | null
  /** OPTIONAL EXTENSION. Every run on record, completed or not. */
  total_runs?: number
}

// ── Stored-run normalisation ───────────────────────────────────────────────

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** Read the first key that carries a finite number. Returns null when none does. */
function firstNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const found = asNullableNumber(source[key])
    if (found !== null) return found
  }
  return null
}

export function normalizeRunPolicy(raw: unknown): AutoRunPolicyRef {
  const p = (raw ?? {}) as Record<string, unknown>
  return {
    id: asNullableString(p.id) ?? asNullableString(p.policy_id),
    name: asString(p.name ?? p.policy_name, 'Governance rule'),
    outcome: asNullableString(p.outcome),
    detail: asNullableString(p.detail ?? p.rationale),
  }
}

export function normalizeRunDecision(raw: unknown): AutoRunDecision {
  const d = (raw ?? {}) as Record<string, unknown>
  return {
    artifact: asNullableString(d.artifact ?? d.artifact_key ?? d.artifact_title),
    action: asString(d.action, 'decision'),
    // A name, never an id: `approved_by` is the identity claim and is only read
    // here as a last resort, after both spellings of the display name.
    by: asNullableString(d.by ?? d.approved_by_name ?? d.decided_by ?? d.approved_by),
    at: asNullableString(d.at ?? d.approved_at ?? d.decided_at),
    option: asNullableString(d.option ?? d.chosen_option ?? d.choice),
  }
}

export function normalizeRunMessage(raw: unknown, index: number): AutoRunMessage {
  const m = (raw ?? {}) as Record<string, unknown>
  return {
    id: asString(m.id, `message-${index}`),
    sequence: asNumber(m.sequence, index + 1),
    role: oneOf<AutoRunMessageRole>(m.role, AUTO_RUN_MESSAGE_ROLES, 'system'),
    content: asString(m.content),
    routed: m.routed !== false,
    turn_id: asNullableString(m.turn_id),
    created_at: asNullableString(m.created_at),
  }
}

export function normalizeRunStep(raw: unknown, index: number): AutoRunStep {
  const s = (raw ?? {}) as Record<string, unknown>
  return {
    ...normalizeStep(raw, index),
    message_id: asNullableString(s.message_id),
    turn_sequence: typeof s.turn_sequence === 'number' ? s.turn_sequence : null,
    result: s.result ?? null,
    created_at: asNullableString(s.created_at),
    operator_id: asNullableString(s.operator_id),
  }
}

const ARTIFACT_KINDS: ArtifactKind[] = [
  'job_description',
  'email_draft',
  'letter',
  'checklist',
  'decision',
]

export function normalizeRunArtifact(raw: unknown, index: number): AutoRunArtifact {
  const a = (raw ?? {}) as Record<string, unknown>
  return {
    id: asString(a.id, `artifact-${index}`),
    artifact_key: asNullableString(a.artifact_key),
    kind: oneOf<ArtifactKind>(a.kind, ARTIFACT_KINDS, 'letter'),
    title: asString(a.title, 'Draft'),
    recipient: asNullableString(a.recipient),
    subject: asNullableString(a.subject),
    body: asString(a.body),
    edited_body: asNullableString(a.edited_body),
    edited_subject: asNullableString(a.edited_subject),
    status: oneOf<AutoRunArtifactStatus>(a.status, AUTO_RUN_ARTIFACT_STATUSES, 'draft'),
    approved_by: asNullableString(a.approved_by),
    approved_by_name: asNullableString(a.approved_by_name),
    approved_at: asNullableString(a.approved_at),
    simulated: typeof a.simulated === 'boolean' ? a.simulated : null,
    sent_at: asNullableString(a.sent_at),
    created_at: asNullableString(a.created_at),
  }
}

/** The fields a list row and a detail row share. */
function normalizeRunCore(r: Record<string, unknown>, index: number): AutoRunListItem {
  const systems = Array.isArray(r.systems_touched) ? r.systems_touched : []

  return {
    id: asString(r.id, `run-${index}`),
    run_code: asString(r.run_code, 'AUTO'),
    session_id: asNullableString(r.session_id),
    scenario_key: asString(r.scenario_key, 'unknown'),
    title: asString(r.title, 'Auto run'),
    status: oneOf<AutoRunStatus>(r.status, AUTO_RUN_STATUSES, 'in_progress'),
    channel: asString(r.channel, 'portal'),
    initiated_by: asNullableString(r.initiated_by),
    initiated_by_name: asNullableString(r.initiated_by_name),
    started_at: asNullableString(r.started_at),
    completed_at: asNullableString(r.completed_at),
    outcome_summary: asNullableString(r.outcome_summary),
    // An unrecognised system key would render as the knowledge-base badge and
    // quietly overstate the fan-out, so it is dropped instead.
    systems_touched: systems.filter((s): s is SystemKey => SYSTEM_KEYS.includes(s as SystemKey)),
    // Left undefined rather than defaulted to `[]` when the field is absent, so a
    // reader can tell "this run recorded none" from "this payload did not say".
    policies_applied: Array.isArray(r.policies_applied)
      ? r.policies_applied.map(normalizeRunPolicy)
      : undefined,
    decisions_taken: Array.isArray(r.decisions_taken)
      ? r.decisions_taken.map(normalizeRunDecision)
      : undefined,
    step_count: asNumber(r.step_count),
    duration_seconds: asNullableNumber(r.duration_seconds),
    requisition_id: asNullableString(r.requisition_id),
    employee_id: asNullableString(r.employee_id),
    separation_id: asNullableString(r.separation_id),
  }
}

export function normalizeRunListItem(raw: unknown, index: number): AutoRunListItem {
  return normalizeRunCore((raw ?? {}) as Record<string, unknown>, index)
}

export function normalizeRunDetail(raw: unknown): AutoRunDetail {
  const r = (raw ?? {}) as Record<string, unknown>
  const core = normalizeRunCore(r, 0)
  const messages = Array.isArray(r.messages) ? r.messages.map(normalizeRunMessage) : []
  const steps = Array.isArray(r.steps) ? r.steps.map(normalizeRunStep) : []

  return {
    ...core,
    messages,
    steps,
    artifacts: Array.isArray(r.artifacts) ? r.artifacts.map(normalizeRunArtifact) : [],
    slots: (r.slots as Record<string, string> | undefined) ?? {},
    // A detail payload that omits the denormalised count still shows the true one.
    step_count: core.step_count || steps.length,
  }
}

/**
 * Coerce the summary payload.
 *
 * The alias lists exist because this tab and the endpoint behind it were built in
 * parallel: a figure that arrives as `avg_time_to_outcome_seconds` rather than
 * `avg_duration_seconds` is the same figure, and reading both costs one array
 * while guessing wrong costs the headline number on a stand.
 */
export function normalizeRunsSummary(raw: unknown): AutoRunsSummary {
  const s = (raw ?? {}) as Record<string, unknown>

  const avgSeconds = firstNumber(s, [
    'avg_duration_seconds',
    'average_duration_seconds',
    'avg_time_to_outcome_seconds',
  ])
  const avgMinutes = firstNumber(s, ['avg_duration_minutes', 'avg_time_to_outcome_minutes'])

  return {
    runs_completed: firstNumber(s, ['runs_completed', 'completed_runs']) ?? 0,
    systems_orchestrated: firstNumber(s, ['systems_orchestrated', 'distinct_systems']) ?? 0,
    human_decisions_recorded:
      firstNumber(s, ['human_decisions_recorded', 'decisions_recorded', 'human_decisions']) ?? 0,
    avg_duration_seconds: avgSeconds ?? (avgMinutes === null ? null : avgMinutes * 60),
    total_runs: firstNumber(s, ['total_runs']) ?? undefined,
  }
}
